import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import i18next from 'i18next';

import AIMS_FLAVORS from 'lib/aimsFlavors';
import Breakpoints from 'lib/Breakpoints';
import getNavbarHeight from 'lib/getNavbarHeight';

import IntObserver from 'components/IntObserver';
import Placeholder from 'components/Picture/Placeholder';

const block = 'with-sticky';

export function WithSticky(WrappedComponent) {
  return class extends React.Component {
    static propTypes = {
      stickyEnabled: PropTypes.bool,
    };

    static contextTypes = {
      setStickyVideoFlag: PropTypes.func,
    };

    static defaultProps = {
      stickyEnabled: false,
    };

    constructor(props) {
      super(props);
      this.wrapperRef = React.createRef();
      this.state = {
        enabled: props.stickyEnabled,
        shouldStick: false,
        stuck: false,
        closed: false,
      };
    }

    isComponentScrolledOutOfView = (depth = 0) => {
      const navbarHeight = getNavbarHeight(Breakpoints);

      // Containing element from parent component
      const {
        current: {
          parentElement: parent,
        },
      } = this.wrapperRef;

      // Get dimensions of the container to check relative scroll position
      const {
        top: containerTop,
        height: containerHeight,
      } = parent.getBoundingClientRect();

      const topOffset = containerTop + depth;
      const bottomOffset = topOffset + containerHeight;

      return (
        (bottomOffset < (depth + navbarHeight)) // scrollY is below the bottom of the container
        || (topOffset > (depth + window.innerHeight)) // scrollY is above the top of the container
      );
    }

    handleObserverCallback = ([entry]) => {
      const {
        enabled,
        shouldStick,
        stuck,
      } = this.state;

      if (!enabled) {
        return;
      }

      const { isIntersecting } = entry;

      if (isIntersecting && shouldStick && stuck) {
        this.handleUnstick();
      } else if (!isIntersecting && shouldStick && !stuck) {
        this.handleStick();
      }
    }

    handleStick = () => {
      this.setState({ stuck: true });
      this.handleStickVideoFlag(true);
    }

    handleUnstick = () => {
      this.setState({ stuck: false });
      this.handleStickVideoFlag(false);
    }

    handleShouldStick = () => {
      this.setState({ shouldStick: true });
    }

    handleShouldUnstick = () => {
      this.setState({ shouldStick: false });
    }

    handleStickVideoFlag = (flag) => {
      const { setStickyVideoFlag } = this.context;
      if (setStickyVideoFlag) {
        setStickyVideoFlag(flag);
      }
    }

    handleClose = () => {
      this.handleUnstick();
      this.setState({ closed: true, shouldStick: false });
    }

    renderCloseButton = () => {
      const { enabled } = this.state;
      if (enabled) {
        return (
          <div className={`${block}__close`}>
            <button
              type="button"
              className={`${block}__close-button icon icon-close`}
              onClick={this.handleClose}
            >
              <span className={`${block}__close-label`}>
                {i18next.t('Close')}
              </span>
            </button>
          </div>
        );
      }
      return null;
    }

    render() {
      // Remove sticky specific props
      const {
        stickyEnabled,
        ...props
      } = this.props;

      if (!stickyEnabled) {
        return (
          <WrappedComponent
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
          />
        );
      }

      const { closed, stuck } = this.state;
      const navbarHeight = getNavbarHeight(Breakpoints);

      return (
        <IntObserver
          callback={this.handleObserverCallback}
          rootMargin={`${navbarHeight}px 0px 0px 0px`}
          threshold={0.3}
        >
          <Placeholder responsiveFlavors={{ s: AIMS_FLAVORS.FOCAL_860x484 }} />
          <div
            className={classNames(block, { [`${block}--stuck`]: stuck })}
          >
            <WrappedComponent
              stickyClosed={closed}
              stickyShouldStick={this.handleShouldStick}
              stickyShouldUnstick={this.handleShouldUnstick}
              stickyStuck={stuck}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...props}
            />
            {this.renderCloseButton()}
          </div>
        </IntObserver>
      );
    }
  };
}
